<template>
    <div class="europamaut-order-form-container">
        <form class="europamaut-order-form" action="" method="post" ref="europamautOrderForm">
            <div class="row">
                <div class="col-12">
                    <h4 id="flagSelectionTitle" class="mt-0 mb-2 text-primary">
                        {{ $t('shopEuropamaut.flagSelectionTitle') }}
                    </h4>
                </div>
                <div class="col-12 flag-selection-wrapper text-center">
                    <div v-for="activeSystem in activeSystems" :key="activeSystem.systemKey" class="flag-selection">
                        <div class="text-center mb-2">
                            <span class="system-lang" v-html="$t(activeSystem.lang)"></span>
                        </div>
                        <a class="overlay-container" @click="selectSystem(activeSystem)">
                            <div class="overlay-wrapper">
                                <img
                                    class="country-flag mx-3"
                                    :class="{ 'country-flag-selected': systemIsSelected(activeSystem.systemKey) }"
                                    :alt="activeSystem.systemKey"
                                    :src="activeSystem.flag"
                                    loading="lazy"
                                />
                                <template v-if="isMobile">
                                    <i
                                        :class="systemIsSelected(activeSystem.systemKey) ? 'icon-selected' : 'd-none'"
                                        class="mdi mdi-plus-circle-outline"
                                    ></i>
                                </template>
                                <template v-else>
                                    <i
                                        :class="systemIsSelected(activeSystem.systemKey) ? 'icon-selected' : 'd-none'"
                                        class="mdi mdi-check-circle-outline"
                                    ></i>
                                </template>
                            </div>
                        </a>
                        <template v-if="!isMobile">
                            <div
                                v-if="!systemIsSelected(activeSystem.systemKey)"
                                class="mt-1 text-center icon-selction"
                            >
                                <a class="select-flag" @click="selectSystem(activeSystem)">
                                    <i class="mdi mdi-plus-circle-outline icon"></i>
                                </a>
                            </div>
                            <div class="mt-1 text-center align-items-center icon-selection" v-else>
                                <a class="select-flag" @click="unselectSystem(activeSystem.systemKey)">
                                    <i class="mdi mdi-minus-circle-outline icon"></i>
                                </a>
                                <span>
                                    {{ getSystemCount(activeSystem.systemKey) }}
                                </span>
                                <a class="select-flag" @click="selectSystem(activeSystem)">
                                    <i class="mdi mdi-plus-circle-outline icon"></i>
                                </a>
                            </div>
                        </template>
                    </div>
                    <div v-if="productError" class="col-12 mt-2">
                        <span class="text-danger">
                            {{ $t('shopEuropamaut.noSelectedProduct') }}
                        </span>
                    </div>
                </div>
                <div id="productSelectionTitle" class="col-12 mt-3" v-if="systemComponents.length > 0">
                    <h4>{{ $t('shopEuropamaut.productSelectionTitle') }}</h4>
                </div>
                <template v-for="component in systemComponents" :key="component.id">
                    <component
                        class="mt-2 col-12 product-selection-wrapper"
                        :id="component.id"
                        :is="component.name"
                        v-bind="{ ...component.props }"
                        :ref="component.id"
                        :is-mobile="isMobile"
                        @product-updated="updateSelectedProducts"
                        @close-component="closeComponent(component.id)"
                    ></component>
                </template>
                <div id="userInputForm" class="col-12" :class="{ 'd-none': selectedProducts.length <= 0 }">
                    <user-input
                        :states="licensePlates"
                        :input-templates="licenseInputTemplates"
                        :session-voucher="this.sessionVoucher"
                        :selected-products="selectedProducts"
                        :product-type="productType"
                        :providers-popup-enabled="providersPopupEnabled"
                        :enable-expiry-of-right-of-withdrawal-setting="this.enableExpiryOfRightOfWithdrawalSetting"
                        ref="userInputs"
                        @product-error="onProductError"
                        @submit-form="onFormSubmit"
                        @duration-error="onDurationError"
                        @vehicle-error="onVehicleError"
                    ></user-input>
                </div>
            </div>
        </form>
    </div>
</template>

<style lang="scss" scoped>
.overlay-container {
    display: flex;
    justify-content: center;
}

.overlay-wrapper {
    position: relative;
}

.flag-selection-wrapper,
.product-selection-wrapper {
    background: #f0f8f7;
    padding: 10px;
    border-radius: 15px;
}

.icon {
    font-size: 28px;
}

.icon-selection {
    width: 82px;
    display: flex;
    justify-content: space-between;
    margin-left: 16px;
}

.icon-selected {
    filter: brightness(0) invert(1);
    position: absolute;
    font-size: 2rem;
    cursor: pointer;
    left: 36%;
    bottom: 0;
}

.system-lang {
    display: inline-block;
    max-width: min-content;
    font-size: 16px;
}

.flag-selection {
    display: inline-block;
}

.select-flag {
    cursor: pointer;
}

.country-flag {
    height: auto;
    width: 82px;
    border-radius: 15px;
    display: block;
    -webkit-box-shadow: 0px 0px 20px 2px rgba(0, 0, 0, 0.15);
    -moz-box-shadow: 0px 0px 20px 2px rgba(0, 0, 0, 0.15);
    box-shadow: 0px 0px 20px 2px rgba(0, 0, 0, 0.15);
}

.country-flag:hover:not(.country-flag-selected) {
    cursor: pointer;
    filter: brightness(0.9);
}

.country-flag-selected {
    transition: all 0.6s ease;
    filter: brightness(0.6);
    opacity: 1.3;
}

@media only screen and (max-width: 767px) {
    .flag-selection {
        margin-bottom: 1rem;
        width: 90px;
    }

    .country-flag {
        width: 70px;
    }

    .icon-selected {
        left: 35%;
        top: 0;
    }

    .system-lang {
        font-size: 14px;
    }
}
</style>

<script>
import ProductSelection from './ProductSelection.vue';
import UserInput from './UserInput.vue';

export default {
    components: {
        ProductSelection,
        UserInput,
    },

    props: [
        'activeSystems',
        'licensePlates',
        'licenseInputTemplates',
        'datepickerConfig',
        'productType',
        'providersPopupEnabled',
        'sessionVoucher',
        'preselectSystems',
        'enableExpiryOfRightOfWithdrawalSetting',
    ],

    data() {
        return {
            durations: {},
            validity: {},
            datePickerOptions: this.$datePickerOptions,
            systemComponents: [],
            selectedProducts: [],
            userData: [],
            productError: false,
            windowWidth: window.innerWidth,
        };
    },

    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },

    watch: {
        systemComponents() {
            this.updateSelectedProducts();
        },
    },

    computed: {
        isMobile() {
            return this.windowWidth <= 1024;
        },
    },

    mounted() {
        this.$nextTick(() => {
            window.addEventListener('resize', this.onResize);
        });

        this.scrollToId('flagSelectionTitle');

        const params = new URLSearchParams(window.location.search);
        if (params.has('c')) {
            const preselection = this.parseUrlParams(params.get('c'));
            if (preselection) {
                preselection.forEach((entry) => {
                    const system = this.activeSystems.find((v) => v.systemKey === entry.sys);
                    if (system) {
                        this.selectSystem(system, false, entry);
                    }
                });

                this.scrollToId('userInputForm');
            }
        } else if (this.preselectSystems) {
            let firstId = null;
            this.preselectSystems.forEach((item) => {
                const system = this.activeSystems.find((v) => v.systemKey === item);
                const id = this.selectSystem(system, false);
                firstId ??= id;
            });
            if (this.isMobile && firstId) {
                this.scrollToId(firstId);
            }
        }
    },

    methods: {
        onResize() {
            this.windowWidth = window.innerWidth;
        },

        parseUrlParams(base64) {
            try {
                const decodedString = atob(base64);

                return JSON.parse(decodedString);
            } catch (error) {
                return null;
            }
        },

        selectSystem(system, scroll = true, preselection = null) {
            if (preselection === null && this.getSystemCount(system.systemKey) >= 3) {
                return;
            }

            const systemProducts = Object.values(system.products);
            const vehicles = this.getProductVehicleOptions(systemProducts);
            const durations = this.getVehicleDurations(systemProducts, vehicles);
            const id = 'product-selection-' + (Math.random() + 1).toString(36).substring(2);
            const props = {
                system: system.systemKey,
                vehicles: vehicles,
                durations: durations,
                products: systemProducts,
                datepickerConfig: this.datepickerConfig,
                hasDurationError: false,
                hasVehicleError: false,
                systemConfig: system,
                preselectedVehicle: parseInt(preselection?.v) ?? null,
                preselectedProduct: parseInt(preselection?.p) ?? null,
                preselectedDate: preselection?.s,
            };

            this.systemComponents.push({ name: 'product-selection', props, id: id });

            if (scroll && this.isMobile) {
                if (this.systemComponents.length > 1) {
                    this.scrollToId(id);
                } else {
                    this.scrollToId('productSelectionTitle');
                }
            }

            return id;
        },

        unselectSystem(system) {
            this.systemComponents.splice(
                this.systemComponents.findIndex((x) => x.props.system === system),
                1
            );
        },

        getProductVehicleOptions(systemProducts) {
            let maxOptionsLength = -Infinity;
            let maxProduct;

            systemProducts.forEach((product) => {
                if (product.runtimes.length > 0) {
                    const optionLength = product.options.length;

                    if (optionLength > maxOptionsLength) {
                        maxProduct = product;
                        maxOptionsLength = optionLength;
                    }
                }
            });

            if (maxProduct && maxProduct['options']) {
                maxProduct['options'] = maxProduct['options'].filter((opt) => {
                    return opt.isDisabledForCombinedOrder !== true;
                });
            }

            return maxProduct['options'] ?? [];
        },

        getVehicleDurations(products, vehicles) {
            const durations = {};

            products.forEach((product) => {
                vehicles.forEach((vehicle) => {
                    const hasVehicle = product.options.find((opt) => {
                        return opt.type === vehicle.type;
                    });
                    if (hasVehicle && hasVehicle.isDisabledForCombinedOrder !== true) {
                        // Check if prop exists already
                        if (durations[vehicle.id]) {
                            durations[vehicle.id].push(product.duration);
                        } else {
                            durations[vehicle.id] = [product.duration];
                        }

                        durations[vehicle.id].sort((a, b) => {
                            a = a.isMonth ? Math.round(a.value * 30) : a.value;
                            b = b.isMonth ? Math.round(b.value * 30) : b.value;

                            return a - b;
                        });
                    }
                });
            });

            return durations;
        },

        systemIsSelected(system) {
            return this.systemComponents.find((x) => x.props.system === system);
        },

        getSystemCount(system) {
            return this.systemComponents.filter((x) => x.props.system === system).length;
        },

        closeComponent(id) {
            this.systemComponents = this.systemComponents.filter((component) => component.id !== id);

            if (this.systemComponents.length <= 0) {
                this.scrollToId('flagSelectionTitle');
            } else {
                const lastUnconfirmedBox = this.selectedProducts.reverse().find((product) => {
                    return product.selectionDone === false && product.componentId !== id;
                });

                if (lastUnconfirmedBox) {
                    this.scrollToId(lastUnconfirmedBox.componentId);
                }
            }
        },

        scrollToId(componentId, timeout = 50) {
            setTimeout(() => {
                const componentEl = document.getElementById(componentId);
                if (componentEl) {
                    const topNavHeight = document.querySelector('#topnav').getBoundingClientRect().height;
                    const windowY = window.scrollY;

                    window.scrollTo({
                        top: windowY + componentEl.getBoundingClientRect().top - topNavHeight,
                        behavior: 'smooth',
                    });
                }
            }, timeout);
        },

        updateSelectedProducts() {
            this.$nextTick(() => {
                this.selectedProducts = [];

                if (this.systemComponents.length <= 0) {
                    return;
                }

                this.systemComponents.forEach((component) => {
                    const ref = this.$refs[component.id][0];
                    const product = Object.assign({}, ref['selectedProduct']);
                    const vehicle = Object.assign({}, ref['selectedVehicle']);
                    const listingId = 'product-listing-' + (Math.random() + 1).toString(36).substring(2);
                    const componentId = component.id;
                    const option = product.options.find((opt) => {
                        return (
                            (opt.type === vehicle.type && !opt.fuel.type) ||
                            (opt.fuel.type === vehicle.fuel.type && opt.type === vehicle.type)
                        );
                    });

                    // Remove runtimes property to make the request more lightweight
                    delete product['runtimes'];

                    const data = {
                        selectionDone: ref['selectionDone'],
                        product: product,
                        option: option,
                        validity: ref['selectedDateRuntime'],
                        listingId: listingId,
                        componentId: componentId,
                    };
                    this.selectedProducts.push(data);
                });
            });
        },

        onProductError() {
            this.productError = true;
        },

        onFormSubmit() {
            this.productError = false;

            for (let i = 0; i < this.systemComponents.length; i++) {
                this.systemComponents[i]['props']['hasDurationError'] = false;
                this.systemComponents[i]['props']['hasVehicleError'] = false;
            }
        },

        onVehicleError(optionErrors) {
            this.selectedProducts.forEach((selectedProduct) => {
                const foundSelectedOption = optionErrors.filter((optError) => {
                    return selectedProduct.option.id === optError.id && selectedProduct.option.type === optError.type;
                });

                if (foundSelectedOption.length > 0) {
                    const component = this.systemComponents.find(
                        (component) => component.id === selectedProduct.componentId
                    );
                    component.props['hasVehicleError'] = true;
                }
            });
        },

        onDurationError(durationErrors) {
            durationErrors.forEach((err) => {
                this.handleDurationError(err);
            });
        },

        handleDurationError(error) {
            if (this.systemComponents.length > 0) {
                const product = error['product'];
                const system = error['system'];

                const occurrences = this.selectedProducts.filter((selectedProduct) => {
                    return selectedProduct.product.id === product && selectedProduct.product.system.key === system;
                });

                let skipFirstEntry = occurrences.length > 1;
                let scrollToError = false;

                this.systemComponents.forEach((component, index) => {
                    const ref = this.$refs[component.id][0];
                    if (
                        ref &&
                        typeof ref.selectedProduct === 'object' &&
                        ref.selectedProduct.id === product &&
                        ref.selectedProduct.system.key === system
                    ) {
                        if (skipFirstEntry) {
                            skipFirstEntry = false;
                        } else {
                            this.systemComponents[index]['props']['hasDurationError'] = true;

                            if (!scrollToError) {
                                scrollToError = true;

                                setTimeout(() => {
                                    const elementY = document.getElementById(component.id).getBoundingClientRect().top;
                                    const topNavHeight = document
                                        .querySelector('#topnav')
                                        .getBoundingClientRect().height;
                                    const windowY = window.scrollY;

                                    window.scrollTo(0, windowY + elementY - topNavHeight);
                                }, 50);
                            }
                        }
                    }
                });
            }
        },
    },
};
</script>
